import {Maybe, OpeningHourCollectionType} from '../gql/pwa/graphql';
import {format} from 'date-fns';
import {getDayName} from './days_from_now.ts';
import {__} from './lang/translations.ts';
import {Customer} from '../gql/pwa/graphql';
import {Vehicle} from '../gql/customer/graphql.ts';

export enum CustomerFieldKeys {
    NAME = 'name',
    SURNAME = 'surname',
    BIRTHDAY = 'birthday',
    EMAIL = 'email',
    COUNTRY = 'country',
    LANGUAGE = 'language',
    PREPOSITION = 'preposition',
    STREET = 'street',
    HOUSE_NUMBER = 'house_number',
    HOUSE_NUMBER_ADDITION = 'house_number_addition',
    POSTAL_CODE = 'postal_code',
    CITY = 'city',
    PHONE_NUMBER = 'phone_number',
    EMAIL_BUSINESS = 'email_business',
    EMAIL_OTHER = 'email_other',
    GENDER = 'gender',
}

export enum CustomerPreferences {
    RECEIVE_MARKETING_MESSAGES = 'receive_marketing_messages',
    RECEIVE_SERVICE_MESSAGES = 'receive_service_messages',
    RECEIVE_PERSONAL_MESSAGES = 'receive_personal_messages',
    RECEIVE_GENERAL_MESSAGES = 'receive_general_messages',
    RECEIVE_EMAIL_CHANNEL = 'receive_email_channel',
    RECEIVE_PHONE_CHANNEL = 'receive_phone_channel',
    RECEIVE_WHATSAPP_CHANNEL = 'receive_whatsapp_channel',
    RECEIVE_SMS_CHANNEL = 'receive_sms_channel',
    RECEIVE_MOBILE_PHONE_CHANNEL = 'receive_mobile_phone_channel',
    RECEIVE_MAIL_CHANNEL = 'receive_mail_channel',
    EMAIL_REMINDER_NOTIFICATION = 'email_reminder_notification',
    RECEIVE_PUSH_NOTIFICATION_CHANNEL = 'receive_push_notification_channel',
}

export function getValue(customer: Customer | undefined | null, key: CustomerFieldKeys | string) {
    return customer?.data?.find((d) => d?.key != null && d.key === key);
}

export function getPreference(customer: Customer | undefined | null, key: CustomerPreferences) {
    const preferences = customer?.preferences;
    if (preferences && key in preferences) {
        return preferences[key];
    }

    return null;
}

export function hasDisabledPreference(customer: Customer | undefined | null) {
    const preferences = customer?.preferences;
    if (preferences) {
        return (
            preferences?.receive_general_messages === false ||
            preferences?.receive_marketing_messages === false ||
            preferences?.receive_personal_messages === false ||
            preferences?.receive_service_messages === false ||
            preferences?.receive_email_channel === false ||
            preferences?.receive_mail_channel === false ||
            preferences?.receive_mobile_phone_channel === false ||
            preferences?.receive_phone_channel === false ||
            preferences?.receive_sms_channel === false ||
            preferences?.receive_whatsapp_channel === false
        );
    }

    return true;
}

export function getOpeningHours(openingHoursData: Maybe<OpeningHourCollectionType>[], department: string) {
    const openingHoursForDepartment = openingHoursData.find(
        (openingHourCollection) => openingHourCollection && openingHourCollection.type === department,
    );
    const openingHours: {day: string; time: string}[] = [];

    if (
        !openingHoursForDepartment ||
        !openingHoursForDepartment.opening_hours ||
        openingHoursForDepartment.opening_hours?.length === 0
    ) {
        return openingHours;
    }

    const date = new Date();
    date.setHours(0, 0, 0);

    for (let i = 0; i < 7; i++) {
        const formattedDate = format(date, 'yyyy-MM-dd');
        const openingHour = openingHoursForDepartment.opening_hours.filter(
            (openingHours) => openingHours && openingHours.start?.includes(formattedDate),
        );
        if (openingHour.length > 0) {
            let times: string = '';
            openingHour.forEach((openingHourItem) => {
                if (openingHourItem && openingHourItem.start && openingHourItem.end) {
                    const startTime = openingHourItem.start.replace(`${formattedDate} `, '');
                    const endTime = openingHourItem.end.replace(`${formattedDate} `, '');
                    times += `${startTime} - ${endTime} \n `;
                }
            });
            if (times.length > 0) {
                openingHours.push({
                    day: getDayName(date.toDateString()),
                    time: times,
                });
            } else {
                openingHours.push({
                    day: getDayName(date.toDateString()),
                    time: <string>__('contact.table.days.closed'),
                });
            }
        } else {
            openingHours.push({
                day: getDayName(date.toDateString()),
                time: <string>__('contact.table.days.closed'),
            });
        }
        date.setDate(date.getDate() + 1);
    }

    return openingHours;
}
//
export function getBreakTimes(breakTimeData: Maybe<OpeningHourCollectionType>[], department: string) {
    const breakTimesForDepartment = breakTimeData.find(
        (breakHourCollection) => breakHourCollection && breakHourCollection.type === department,
    );
    const breakHours: {day: string; time: string}[] = [];

    if (
        !breakTimesForDepartment ||
        !breakTimesForDepartment.break_times ||
        breakTimesForDepartment.break_times?.length === 0
    ) {
        return breakHours;
    }

    const date = new Date();
    date.setHours(0, 0, 0);

    for (let i = 0; i < 7; i++) {
        const formattedDate = format(date, 'yyyy-MM-dd');
        const breakTime = breakTimesForDepartment.break_times.find(
            (openingHours) => openingHours && openingHours.start?.includes(formattedDate),
        );
        if (breakTime && breakTime.start && breakTime.end) {
            const startTime = breakTime.start.replace(`${formattedDate} `, '');
            const endTime = breakTime.end.replace(`${formattedDate} `, '');
            breakHours.push({
                day: getDayName(date.toDateString()),
                time: `${startTime} - ${endTime}`,
            });
        } else {
            breakHours.push({
                day: getDayName(date.toDateString()),
                time: '',
            });
        }
        date.setDate(date.getDate() + 1);
    }

    return breakHours;
}

export function getLicensePlate(vehicle: Vehicle | undefined | null) {
    if (vehicle) {
        const licensePlateIdentifier = vehicle.identifiers?.find(
            (identifier) => identifier !== null && identifier.type === 'license_plate',
        );
        if (licensePlateIdentifier) {
            return licensePlateIdentifier.value ?? '';
        }
    }

    return '';
}
